/**
 * @fileoverview The Lessons page — every lesson the instructor has taught,
 * with the filter bar, the metrics rail and the spreadsheet export.
 *
 * This is a HISTORY HUB, not a feed, and that changes how it loads. The old
 * page paged 50 rows at a time, which is right for scrolling and wrong for
 * everything else here: a period filter, a set of totals and an export all
 * have to see the whole history or they quietly describe page one. So the
 * page pulls the full list once (500 at a time until a short page comes back)
 * and every filter, number and export runs client-side against that array —
 * which is also what makes changing the period instant.
 *
 * Known follow-up: `GET /lessons` returns whole lesson documents, summaries
 * and practice items included, so a very large studio pays for content this
 * page never renders. The fix is a lightweight projection or a server-side
 * aggregate, not re-introducing pagination — see the note on the fetch below.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import api from "@/lib/api";
import { AuthContext } from "@/App";
import { uploadQueue } from "@/lib/uploadQueue";
import { savedCountLabel } from "@/lib/savedRecordings";
import { formatLessonDuration } from "@/lib/duration";
import {
  DEFAULT_PERIOD,
  PERIODS,
  buildExportCsv,
  buildExportFilename,
  classesInHistory,
  filterLessons,
  isClassLesson,
  isShared,
  isPlanned,
  lessonDate,
  recordedAt,
  sortChronologically,
} from "@/lib/lessonHistory";
import { Button, Card, Eyebrow, Icon } from "@/components/studio/primitives";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import LessonExportDialog from "@/components/studio/LessonExportDialog";
import LessonHistoryMetrics from "@/components/studio/LessonHistoryMetrics";
import { MoreRowsBar, usePagedRows } from "@/components/studio/MoreRows";
import ProgramChip from "@/components/ProgramChip";
import { SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";

/** Server cap. One request covers a year for almost every studio. */
const PAGE_SIZE = 500;
/** Stop after this many pages so a pathological account can't spin forever. */
const MAX_PAGES = 20;

const DATE_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });

/* ------------------------------------------------------------------ */
/*  Row                                                                */
/* ------------------------------------------------------------------ */

/**
 * A substitute's lesson in one of the reader's classes (docs/substitute-requests.md):
 * listed so the class's history reads whole, but not the reader's — greyed,
 * read-only, and outside every figure on the page. One the reader wrote while
 * covering for a colleague (`read_only: false`) is theirs, and counts.
 */
const isSubstituteLesson = (lesson) => Boolean(lesson?.substitute) && lesson.read_only !== false;

function LessonRow({ lesson, name, onPress }) {
  const isClass = isClassLesson(lesson);
  const shared = isShared(lesson);
  const planned = isPlanned(lesson);
  const date = lessonDate(lesson);
  const substitute = isSubstituteLesson(lesson);

  return (
    <button
      type="button"
      onClick={onPress}
      data-testid={`all-lesson-${lesson.lesson_id}`}
      className={`group flex w-full items-center gap-4 rounded-[14px] border border-transparent px-3 py-3 text-left transition-colors hover:border-[var(--studio-divider)] hover:bg-[var(--studio-raised)]${substitute ? " opacity-60" : ""}`}
    >
      {/* Kind. Class lessons carry the cool accent everywhere they appear —
          the icon here, the tag beside the title, their bar in the rail — so
          "this one had a group in it" is one consistent signal rather than a
          badge you have to go looking for. */}
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
        style={{ background: isClass ? "var(--accent-cool-dim)" : "var(--accent-yellow-dim)" }}
      >
        <Icon
          name={isClass ? "users" : "music2"}
          size={16}
          strokeWidth={1.8}
          style={{ color: isClass ? "var(--accent-cool)" : "var(--accent-yellow-text)" }}
        />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-sans text-[15px] font-medium text-[var(--text-primary)]">
            {lesson.title || "Untitled lesson"}
          </span>
          {isClass && (
            <span
              className="shrink-0 rounded-full px-1.5 py-[2px] font-mono text-[10px] uppercase tracking-wide"
              style={{ background: "var(--accent-cool-dim)", color: "var(--accent-cool)" }}
            >
              Class
            </span>
          )}
          {lesson.manual && (
            <span className="shrink-0 rounded-full border border-[var(--studio-divider-strong)] px-1.5 py-[2px] font-mono text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">
              Typed
            </span>
          )}
          {substitute && (
            <span
              className="shrink-0 rounded-full border border-[var(--studio-divider-strong)] px-1.5 py-[2px] font-mono text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]"
              data-testid={`all-lesson-substitute-${lesson.lesson_id}`}
            >
              Substitute
            </span>
          )}
          {/* Written ahead of its session. It is listed — the instructor
              typed it — but the meeting has not happened, so nothing here
              or on any tile counts it as taught. */}
          {planned && (
            <span className="shrink-0 rounded-full border border-[var(--studio-divider-strong)] px-1.5 py-[2px] font-mono text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">
              Planned
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-2 font-sans text-[13px] text-[var(--text-tertiary)]">
          <span className="truncate">
            {name || "—"}
            {substitute ? ` · Taught by ${lesson.substitute.name || "a substitute"}` : ""}
          </span>
        </span>
        {(lesson.program_title || SCHEDULER_VIEW_ENABLED) && (
          <span className="mt-1 flex">
            <ProgramChip lesson={lesson} programTitle={lesson.program_title} orgName={lesson.org_name} accent={lesson.org_accent} />
          </span>
        )}
      </span>

      {/* Shared. A tick when the family has it, a hollow ring when they don't
          — never a blank, because "nothing here" reads as "no information"
          when what it actually means is "you still owe them the notes". */}
      <span
        className="flex w-[104px] shrink-0 items-center gap-1.5"
        title={substitute ? "Your organization sends a substitute's notes" : shared ? "Notes shared with the student" : "Notes not sent yet"}
      >
        {substitute ? (
          <span className="font-sans text-[12px] text-[var(--text-quaternary)]">{shared ? "Shared by org" : "Org sends"}</span>
        ) : shared ? (
          <>
            <Icon name="check" size={14} strokeWidth={2.4} style={{ color: "var(--state-success)" }} />
            <span className="font-sans text-[12px]" style={{ color: "var(--state-success)" }}>Shared</span>
          </>
        ) : (
          <>
            <span className="h-[13px] w-[13px] rounded-full border-[1.5px] border-[var(--text-quaternary)]" />
            <span className="font-sans text-[12px] text-[var(--text-quaternary)]">Not sent</span>
          </>
        )}
      </span>

      <span className="w-[52px] shrink-0 text-right font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">
        {formatLessonDuration(lesson.duration_seconds)}
      </span>
      <span className="w-[54px] shrink-0 text-right font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">
        {date ? DATE_FMT.format(date) : "—"}
      </span>
      <Icon
        name="chevronRight"
        size={16}
        className="shrink-0 text-[var(--text-quaternary)] transition-colors group-hover:text-[var(--text-secondary)]"
      />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function AllLessonsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [period, setPeriod] = useState(() => {
    const requested = searchParams.get("period");
    return PERIODS.some((p) => p.id === requested) ? requested : DEFAULT_PERIOD;
  });
  const [kind, setKind] = useState("all");
  /* One class, out of the ones that appear in the history (I10). Seeded from
     the URL so a class page can link straight to its own lessons. */
  const [classId, setClassId] = useState(() => searchParams.get("class") || null);
  const [search, setSearch] = useState("");
  const [unsentOnly, setUnsentOnly] = useState(searchParams.get("unsent") === "1");
  const [exportOpen, setExportOpen] = useState(false);
  // Sessions taught with no notes. Best-effort: a count that fails to load
  // hides the banner rather than blocking the page it sits above.
  const [notesOwed, setNotesOwed] = useState(0);
  // Recordings the recorder could not send, kept in this browser (B6). They
  // are lessons that do not exist yet, so the list names them at its top.
  const { user } = useContext(AuthContext);
  const [savedCount, setSavedCount] = useState(0);
  useEffect(() => {
    let on = true;
    uploadQueue.listForAccount(user?.user_id)
      .then((rows) => { if (on) setSavedCount(rows.length); })
      .catch(() => {});
    return () => { on = false; };
  }, [user?.user_id]);

  const listRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/notes-owed")
      .then(({ data }) => {
        if (!cancelled) setNotesOwed(data?.count || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const studentNameById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(s.student_id, `${s.first_name || ""} ${s.last_name || ""}`.trim());
    return map;
  }, [students]);

  const classNameById = useMemo(() => {
    const map = new Map();
    for (const c of classes) map.set(c.class_id, c.name);
    return map;
  }, [classes]);

  const nameFor = useCallback(
    (lesson) =>
      isClassLesson(lesson)
        ? classNameById.get(lesson.class_id) || "Class lesson"
        : studentNameById.get(lesson.student_id) || "",
    [classNameById, studentNameById]
  );

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        // `status=all` because this list is a NAME MAP over history, not a
        // roster (I2): archiving a student does not hide their past lessons,
        // so the active roster alone would blank the name on every row
        // belonging to someone who has left — and the export with it.
        api
          .get("/students?status=all", { signal: controller.signal })
          .then((res) => setStudents(res.data || []))
          .catch(() => {});
        api
          .get("/classes", { signal: controller.signal })
          .then((res) => setClasses(res.data || []))
          .catch(() => {});

        // Walk the whole history. Metrics, the period filter and the export
        // are all whole-history questions; answering them from page one would
        // put a number on screen that looks authoritative and is not.
        const all = [];
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const res = await api.get(`/lessons?limit=${PAGE_SIZE}&skip=${all.length}`, { signal: controller.signal });
          const batch = res.data || [];
          all.push(...batch);
          if (batch.length < PAGE_SIZE) break;
        }
        if (controller.signal.aborted) return;
        // Oldest first, so the months and the export read the way they were
        // taught; the server pages newest first.
        setLessons(sortChronologically(all, recordedAt));
      } catch (_e) {
        if (!controller.signal.aborted) toast.error("Failed to load lessons");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  // Two filter stages, and the split is deliberate.
  //
  // `scoped` is what the metrics rail measures: the period and the kind — the
  // controls that answer "what am I looking at". `filtered` adds search and
  // the not-sent toggle, which answer "find me one of these". Letting a lookup
  // into the rail makes its own numbers eat themselves: click "4 not sent" and
  // the rail would recompute to say 4 lessons, 4 not sent, and a weekly
  // average of 0.9 that means nothing at all.
  const scoped = useMemo(
    () => filterLessons(lessons, { period, kind, classId }, nameFor),
    [lessons, period, kind, classId, nameFor]
  );
  // The rail measures the reader's own teaching, so a substitute's lesson in
  // one of their classes is listed but never counted.
  const counted = useMemo(() => scoped.filter((l) => !isSubstituteLesson(l)), [scoped]);
  const countedAll = useMemo(() => lessons.filter((l) => !isSubstituteLesson(l)), [lessons]);
  // The classes the history actually holds, so the picker never offers an
  // empty result. Named as the rows name them.
  const classOptions = useMemo(() => classesInHistory(lessons, nameFor), [lessons, nameFor]);
  const filtered = useMemo(
    () =>
      filterLessons(scoped, { period: "all", kind: "all", search, unsentOnly }, nameFor).filter(
        // "Not sent" is a to-do list, and a substitute's notes are not the reader's to send.
        (l) => !(unsentOnly && isSubstituteLesson(l)),
      ),
    [scoped, search, unsentOnly, nameFor]
  );

  /* The first 25 (or 50, or 100) of the period, oldest at the top, with the
     later ones a click below (MoreRows). A new filter opens at the start of
     its lessons again. The rail and the export still cover every filtered row. */
  const paged = usePagedRows(filtered, [period, kind, classId, search, unsentOnly].join("|"));

  /** Shown rows grouped under month headings — the shape of a history. A
      month cut short by the limit says how much of it is showing. */
  const groups = useMemo(() => {
    const monthKey = (d) => (d ? `${d.getFullYear()}-${d.getMonth()}` : "undated");
    const totals = new Map();
    for (const lesson of filtered) {
      const key = monthKey(lessonDate(lesson));
      totals.set(key, (totals.get(key) || 0) + 1);
    }
    const out = [];
    let current = null;
    for (const lesson of paged.shown) {
      const d = lessonDate(lesson);
      const key = monthKey(d);
      if (!current || current.key !== key) {
        current = { key, label: d ? MONTH_FMT.format(d) : "Undated", lessons: [], total: totals.get(key) || 0 };
        out.push(current);
      }
      current.lessons.push(lesson);
    }
    return out;
  }, [filtered, paged.shown]);

  const handleExport = (fieldIds) => {
    const csv = buildExportCsv(filtered, fieldIds, { nameFor });
    // The BOM is what makes Excel on Windows read this as UTF-8 rather than
    // as the local codepage — without it a student named Chloé arrives as
    // "ChloÃ©" in the one artifact that leaves the product.
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = buildExportFilename(period);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportOpen(false);
    toast.success(`Exported ${filtered.length} ${filtered.length === 1 ? "lesson" : "lessons"}`);
  };

  const showUnsent = () => {
    setUnsentOnly((v) => !v);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filtersActive = kind !== "all" || !!search || unsentOnly || !!classId;

  return (
    <div data-testid="all-lessons" className="mx-auto w-full max-w-[1240px] px-5 py-8 md:px-8">
      {/* Export lives with the page title rather than in the filter row: it
          acts on whatever the filters have already produced, so it is a page
          action, not another filter — and five controls in one row is where
          that row started wrapping. */}
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <Eyebrow>Every session</Eyebrow>
          <h1 className="mt-1 font-serif text-[34px] font-light leading-tight tracking-tight text-[var(--text-primary)]">
            Lessons
          </h1>
        </div>
        <Button
          variant="ghost"
          type="button"
          icon="download"
          className="mb-1 shrink-0"
          disabled={!filtered.length}
          onPress={() => setExportOpen(true)}
          data-testid="export-button"
        >
          Export
        </Button>
      </header>

      {/* The other half of the teaching record: sessions that happened and were
          never written up. They cannot appear in the list below — a lesson with
          no notes is a lesson that does not exist — so the backlog is named
          here and opened on its own page. */}
      {savedCount > 0 && (
        <Card className="mb-6" data-testid="lessons-saved-recordings">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-sans text-[14px] text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--accent-yellow-text)]">{savedCountLabel(savedCount)}</span>{" "}
              — kept in this browser until they’re sent.
            </p>
            <Button variant="ghost" onPress={() => navigate("/recordings/saved")} data-testid="lessons-open-saved-recordings">
              Open
            </Button>
          </div>
        </Card>
      )}

      {notesOwed > 0 && (
        <Card className="mb-6" data-testid="lessons-notes-owed">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-sans text-[14px] text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--accent-yellow-text)]">
                {notesOwed} taught {notesOwed === 1 ? "session has" : "sessions have"} no notes
              </span>{" "}
              — they aren't in this list until you write them.
            </p>
            <Button variant="ghost" onPress={() => navigate("/notes-owed")} data-testid="lessons-review-notes">
              Write them
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_336px]">
        {/* ---------------- List ---------------- */}
        <section ref={listRef}>
          {/* Filter bar. One row above the list, so what is being measured is
              always visible with the measurements. The one bar every lessons
              list uses (F4), with this page's kind control and its
              not-sent toggle; a class picker when the history has classes. */}
          <LessonFilterBar
            value={{ period, kind, classId, search }}
            onChange={(next) => {
              if (next.period !== period) setPeriod(next.period);
              if (next.kind !== kind) setKind(next.kind);
              if (next.classId !== classId) setClassId(next.classId);
              if (next.search !== search) setSearch(next.search);
            }}
            kinds
            classes={classOptions}
            statuses={false}
            searchPlaceholder="Search lessons or students"
          >
            <button
              type="button"
              onClick={() => setUnsentOnly((v) => !v)}
              data-testid="unsent-filter"
              aria-pressed={unsentOnly}
              className={[
                "flex h-9 items-center gap-1.5 rounded-full border px-3.5 font-sans text-[13px] transition-colors",
                unsentOnly
                  ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                  : "border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              ].join(" ")}
            >
              Not sent
            </button>
          </LessonFilterBar>

          <div className="mb-3 flex items-center justify-between px-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)]">
              {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "lesson" : "lessons"}`}
            </span>
            {filtersActive && (
              <button
                type="button"
                onClick={() => { setKind("all"); setSearch(""); setUnsentOnly(false); }}
                data-testid="clear-filters"
                className="font-sans text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                Clear filters
              </button>
            )}
          </div>

          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-[62px] rounded-[14px] bg-[var(--studio-raised)] skeleton" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card style={{ padding: 48 }} className="text-center" data-testid="all-lessons-empty">
              <Icon name="mic" size={30} strokeWidth={1.4} className="mx-auto mb-4 text-[var(--text-quaternary)]" />
              <p className="font-sans text-[14px] text-[var(--text-secondary)]">
                {lessons.length ? `No lessons match this view` : "No lessons yet"}
              </p>
              {lessons.length ? (
                <Button
                  variant="ghost"
                  type="button"
                  className="mt-5"
                  onPress={() => { setPeriod("all"); setKind("all"); setSearch(""); setUnsentOnly(false); }}
                  data-testid="all-lessons-show-all"
                >
                  Show all time
                </Button>
              ) : (
                <Button
                  variant="primary"
                  type="button"
                  icon="mic"
                  className="mt-5"
                  onPress={() => navigate("/hans")}
                  data-testid="all-lessons-record"
                >
                  Record lesson
                </Button>
              )}
            </Card>
          ) : (
            <div className="flex flex-col gap-6">
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="mb-1 flex items-baseline gap-3 px-3">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--text-tertiary)]">
                      {group.label}
                    </span>
                    <span className="h-px flex-1 bg-[var(--studio-divider)]" />
                    <span className="font-mono text-[11px] tabular-nums text-[var(--text-quaternary)]">
                      {group.lessons.length < group.total ? `${group.lessons.length} of ${group.total}` : group.lessons.length}
                    </span>
                  </div>
                  {group.lessons.map((lesson) => (
                    <LessonRow
                      key={lesson.lesson_id}
                      lesson={lesson}
                      name={nameFor(lesson)}
                      onPress={() =>
                        navigate(
                          isClassLesson(lesson)
                            ? `/lesson/${lesson.lesson_id}?kind=class`
                            : `/lesson/${lesson.lesson_id}`
                        )
                      }
                    />
                  ))}
                </div>
              ))}
              <MoreRowsBar paged={paged} testId="all-lessons-more" />
            </div>
          )}
        </section>

        {/* ---------------- Metrics rail ---------------- */}
        <aside className="lg:sticky lg:top-8 lg:self-start">
          <LessonHistoryMetrics
            filtered={counted}
            allLessons={countedAll}
            period={period}
            nameFor={nameFor}
            onShowUnsent={showUnsent}
            unsentActive={unsentOnly}
            loading={loading}
          />
        </aside>
      </div>

      <LessonExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        rowCount={filtered.length}
        period={period}
        onExport={handleExport}
      />
    </div>
  );
}
